import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export const CostChart = ({ costSummary = [] }) => {
  // Transform cost summary data for the chart
  const chartData = useMemo(() => {
    if (!costSummary || costSummary.length === 0) return [];

    // Take top 10 most expensive endpoints/models
    const sorted = [...costSummary]
      .sort((a, b) => b.total_cost - a.total_cost)
      .slice(0, 10);

    return sorted.map((item) => ({
      name: `${item.provider}/${item.model_id}`.substring(0, 20),
      cost: parseFloat(item.total_cost.toFixed(4)),
      calls: item.total_calls,
      avgDuration: parseFloat(item.avg_duration_ms.toFixed(0)),
    }));
  }, [costSummary]);

  const totalCost = useMemo(() => {
    return costSummary.reduce((sum, item) => sum + item.total_cost, 0);
  }, [costSummary]);

  const totalCalls = useMemo(() => {
    return costSummary.reduce((sum, item) => sum + item.total_calls, 0);
  }, [costSummary]);

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cost by Model</CardTitle>
          <CardDescription>No cost data available</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center text-muted-foreground">
          No data to display
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cost by Model</CardTitle>
        <CardDescription>
          Top 10 models by total cost • ${totalCost.toFixed(4)} total • {totalCalls} calls
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              angle={-45}
              textAnchor="end"
              height={100}
              fontSize={12}
            />
            <YAxis 
              label={{ value: 'Cost (USD)', angle: -90, position: 'insideLeft' }}
              fontSize={12}
            />
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white p-3 border rounded shadow-lg">
                      <p className="font-semibold">{data.name}</p>
                      <p className="text-sm text-gray-600">Cost: ${data.cost}</p>
                      <p className="text-sm text-gray-600">Calls: {data.calls}</p>
                      <p className="text-sm text-gray-600">Avg Duration: {data.avgDuration}ms</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="cost" fill="#6366f1" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
